import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Icon, toast } from "@sagot/ui";
import { useRef, useState, type ChangeEvent } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useImportEndUsers, type ImportResult } from "./use-audiences-data";

/** Backend `CSV_IMPORT_COLUMNS` (services/audiences/csv_import.py). */
export const CSV_COLUMNS = "name,email,external_id,role,segment,audiences";
const CSV_ACCEPT = ".csv,text/csv";

/** Import outcome: counts + per-row errors (row number and reason only, never row content). */
export function ImportResultView({ result }: { result: ImportResult }) {
  const { t } = useTranslation();
  const counts = [
    { key: "created", value: result.created, label: t("deployKnowledge.audiences.importCreated") },
    { key: "updated", value: result.updated, label: t("deployKnowledge.audiences.importUpdated") },
    { key: "skipped", value: result.skipped, label: t("deployKnowledge.audiences.importSkipped") },
  ];
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-3 gap-3 text-xs">
        {counts.map((c) => (
          <div key={c.key} className="rounded-md bg-surface-2 p-2.5">
            <dt className="text-fg-subtle">{c.label}</dt>
            <dd className="mt-0.5 text-lg font-extrabold tabular-nums">{c.value}</dd>
          </div>
        ))}
      </dl>
      {result.errors.length > 0 ? (
        <div>
          <h3 className="text-sm font-bold">{t("deployKnowledge.audiences.importErrors", { count: result.errors.length })}</h3>
          <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs">
            {result.errors.map((error) => (
              <li key={`${String(error.row)}-${error.message}`} className="flex gap-2">
                <span className="shrink-0 font-mono text-fg-subtle">{t("deployKnowledge.audiences.importRow", { row: error.row })}</span>
                <span className="text-danger">{error.message}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="flex items-center gap-1.5 text-sm text-success">
          <Icon name="check-circle" className="size-4" />
          {t("deployKnowledge.audiences.importClean")}
        </p>
      )}
    </div>
  );
}

/** "Import CSV" button: picks a file, uploads its text, then shows the result. */
export function CsvImportButton() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const input = useRef<HTMLInputElement>(null);
  const upload = useImportEndUsers();
  const [result, setResult] = useState<ImportResult | null>(null);
  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const text = await file.text();
    upload.mutate(text, { onSuccess: setResult, onError: (caught) => toast.error(message(caught)) });
  };
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={CSV_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-label={t("deployKnowledge.audiences.importCsv")}
        onChange={(event) => void onPick(event)}
      />
      <Button variant="secondary" size="sm" loading={upload.isPending} title={t("deployKnowledge.audiences.importHint", { columns: CSV_COLUMNS })} onClick={() => input.current?.click()}>
        <Icon name="upload" />
        {t("deployKnowledge.audiences.importCsv")}
      </Button>
      {result ? (
        <Dialog open onOpenChange={(open) => { if (!open) setResult(null); }}>
          <DialogContent
            title={t("deployKnowledge.audiences.importTitle")}
            description={t("deployKnowledge.audiences.importHint", { columns: CSV_COLUMNS })}
            closeLabel={t("common.close")}
            footer={<Button onClick={() => { setResult(null); }}>{t("common.close")}</Button>}
          >
            <ImportResultView result={result} />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
